import { create } from 'zustand';
import { createDefaultPlayerState, type PlayerState } from '../shared/movement';
import { GRENADE } from '../shared/constants';
import { awardMatchXP } from '../game/playerProfile';
import { GRENADE_IDS, PACK_IDS, WEAPON_IDS } from '../shared/game';
import { titleCase as sharedTitleCase } from '../shared/stringUtils';
import type { KillEvent } from '../shared/killTypes';
import type { ArmorClass as SharedArmorClass, PackId as SharedPackId, TeamId as SharedTeamId, WeaponId as SharedWeaponId } from '../shared/game';
import { DEFAULT_MAP_ID, type MapId, type SiegeGameState } from '../shared/mapData';
import type {
  ChatMessage,
  MatchEvent,
  MatchAwardSnapshot,
  MatchSnapshot,
  PlayerScoreboardSnapshot,
  PlayerSnapshot,
  ProjectileSnapshot,
  SensorSnapshot,
  ShieldDoorSnapshot,
  BotIntentSnapshot,
  WeaponDebugTelemetrySnapshot,
  WorldSnapshot,
} from '../shared/protocol';
export type { ChatMessage };
import { createStandaloneWorldSnapshot, type StandaloneSessionOptions } from '../shared/standaloneWorld';
import { routeZoneLabel, summarizeDirectiveCall } from '../shared/teamplay';

type TeamId = SharedTeamId | null;
type ArmorClass = SharedArmorClass | null;
type WeaponId = SharedWeaponId | null;
type SnapshotEntity = {
  id?: string;
  team?: TeamId;
  [key: string]: unknown;
};
type PackSummary = {
  slot: number | null;
  id: string | null;
  label: string | null;
  type: string | null;
  state: string | null;
  active: boolean | null;
  ready: boolean | null;
  cooldownSeconds: number | null;
  charges: number | null;
  maxCharges: number | null;
  energy: number | null;
};

type GrenadeSummary = {
  slot: number | null;
  id: string | null;
  label: string | null;
  type: string | null;
  state: string | null;
  active: boolean | null;
  ready: boolean | null;
  cooldownSeconds: number | null;
  charges: number | null;
  maxCharges: number | null;
};

export type TeamDirective = {
  id: string | null;
  team: TeamId;
  call: string | null;
  message: string;
  ttlSeconds: number;
  sourcePlayerId: string | null;
  sourceName: string | null;
};

// Phase 155 — command-map ping waypoint (relayed to teammates by server).
export type PingWaypoint = {
  id: string;
  team: TeamId;
  position: { x: number; y: number; z: number };
  ttlSeconds: number;
  callerId: string | null;
  callerName: string | null;
  kind: 'ping' | 'repair-needed' | null;
  color: string | null;
};

export type TacticalPing = {
  id: string;
  team: TeamId;
  position: { x: number; y: number; z: number };
  ttlSeconds: number;
  kind: string | null;
  label: string | null;
  sourcePlayerId: string | null;
};

export type RouteCaptureZone = {
  id: string;
  position: { x: number; y: number; z: number };
  radius: number;
  ownerTeam: TeamId;
  captureTargetTeam: TeamId;
  captureProgress: number;
  contested: boolean;
  carrierInside: boolean;
  scoringMultiplier: number;
};

export type ObjectiveScoringHint = {
  id: string;
  team: TeamId;
  kind: string;
  message: string;
  points: number;
  multiplier: number;
  ttlSeconds: number;
  playerId: string | null;
  flagTeam: TeamId;
  routeZoneId: string | null;
};

export type BaseFlagInteractionHook = {
  id: string;
  team: TeamId;
  flagTeam: TeamId;
  kind: string;
  position: { x: number; y: number; z: number };
  active: boolean;
  intensity: number;
  status: 'stable' | 'warning' | 'critical';
  detail: string;
  relatedEntityId: string | null;
};

export type CtfMacroRole = 'carrier' | 'chaser' | 'returner' | 'stand-defense' | 'standoff' | 'escort' | 'neutral';

export type CtfMacroSummary = {
  role: CtfMacroRole;
  title: string;
  status: string;
  cue: string;
  tone: string;
  urgency: 'normal' | 'warning' | 'critical';
  ttlSeconds: number | null;
  team: TeamId;
  flagTeam: TeamId;
  routeZoneId: string | null;
  returnWindowSeconds: number | null;
  standoffSeconds: number | null;
  objectiveTelemetry?: Array<{
    team: TeamId;
    grabs: number;
    returns: number;
    captures: number;
    carrierKills: number;
    standoffs: number;
    generatorDestroys: number;
    generatorRepairs: number;
  }>;
};

function normalizeTeamId(value: unknown): TeamId {
  if (value === 'red' || value === 'blue') {
    return value;
  }

  return null;
}

function normalizeArmorClass(value: unknown): ArmorClass {
  if (value === 'light' || value === 'medium' || value === 'heavy') {
    return value;
  }

  return null;
}

function normalizeWeaponId(value: unknown): WeaponId {
  if (typeof value === 'string' && WEAPON_IDS.includes(value as SharedWeaponId)) {
    return value as SharedWeaponId;
  }

  return null;
}

function normalizePackId(value: unknown): SharedPackId | null {
  if (typeof value === 'string' && PACK_IDS.includes(value as SharedPackId)) {
    return value as SharedPackId;
  }

  return null;
}

function normalizeString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function normalizeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function normalizeBoolean(value: unknown): boolean {
  return Boolean(value);
}

function normalizeCtfMacroRole(value: unknown): CtfMacroRole {
  const key = typeof value === 'string' ? value.toLowerCase() : '';

  if (key.includes('carrier') || key.includes('carry')) {
    return 'carrier';
  }

  if (key.includes('chaser') || key.includes('chase') || key.includes('pursuit') || key.includes('hunt')) {
    return 'chaser';
  }

  if (key.includes('return')) {
    return 'returner';
  }

  if (key.includes('standoff') || key.includes('spacing') || key.includes('holdoff')) {
    return 'standoff';
  }

  if (key.includes('stand') || key.includes('defens') || key.includes('guard') || key.includes('deny')) {
    return 'stand-defense';
  }

  if (key.includes('escort') || key.includes('support')) {
    return 'escort';
  }

  return 'neutral';
}

function normalizeUrgency(value: unknown, fallbackRole: CtfMacroRole): 'normal' | 'warning' | 'critical' {
  const key = typeof value === 'string' ? value.toLowerCase() : '';

  if (key === 'critical' || key === 'high' || key === 'urgent') {
    return 'critical';
  }

  if (key === 'warning' || key === 'medium' || key === 'elevated') {
    return 'warning';
  }

  if (key === 'low' || key === 'normal' || key === 'calm') {
    return 'normal';
  }

  if (fallbackRole === 'carrier' || fallbackRole === 'chaser' || fallbackRole === 'returner' || fallbackRole === 'stand-defense') {
    return 'warning';
  }

  return 'normal';
}

function titleForCtfMacro(role: CtfMacroRole) {
  if (role === 'carrier') {
    return 'CARRIER RUN';
  }

  if (role === 'chaser') {
    return 'CHASE WINDOW';
  }

  if (role === 'returner') {
    return 'RETURN WINDOW';
  }

  if (role === 'stand-defense') {
    return 'STAND DEFENSE';
  }

  if (role === 'standoff') {
    return 'STANDOFF';
  }

  if (role === 'escort') {
    return 'ESCORT';
  }

  return 'CTF MACRO';
}

function toneForCtfMacro(role: CtfMacroRole, team: TeamId) {
  if (team === 'red') {
    return '#ff8f7d';
  }

  if (team === 'blue') {
    return '#62ddff';
  }

  if (role === 'carrier') {
    return '#ffcc66';
  }

  if (role === 'chaser') {
    return '#ff9b71';
  }

  if (role === 'returner') {
    return '#62ddff';
  }

  if (role === 'stand-defense') {
    return '#ffcc66';
  }

  if (role === 'standoff') {
    return '#b58cff';
  }

  if (role === 'escort') {
    return '#8ef0c9';
  }

  return '#9fb4c7';
}

function summarizeCtfMacro(value: unknown): CtfMacroSummary | null {
  if (Array.isArray(value)) {
    for (const entry of value) {
      const normalized = summarizeCtfMacro(entry);
      if (normalized) {
        return normalized;
      }
    }
    return null;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const macro = value as Record<string, unknown>;
  const role = normalizeCtfMacroRole(macro.role ?? macro.kind ?? macro.type ?? macro.mode ?? macro.state);
  const team = normalizeTeamId(macro.team ?? macro.carrierTeam ?? macro.offenseTeam ?? macro.attackingTeam);
  const flagTeam = normalizeTeamId(macro.flagTeam ?? macro.targetTeam ?? macro.defendingTeam ?? macro.objectiveTeam);
  const routeZoneId = normalizeString(macro.routeZoneId ?? macro.routeZone ?? macro.zoneId ?? macro.route ?? macro.laneId);
  const routeLabel = routeZoneLabel(routeZoneId);
  const returnWindowSeconds = Math.max(
    0,
    normalizeNumber(
      macro.returnWindowSeconds ??
        macro.windowSeconds ??
        macro.returnSeconds ??
        macro.remainingReturnSeconds ??
        macro.returnTimerSeconds,
    ) ?? 0,
  );
  const standoffSeconds = Math.max(
    0,
    normalizeNumber(
      macro.standoffSeconds ??
        macro.standOffSeconds ??
        macro.holdSeconds ??
        macro.spacingSeconds ??
        macro.bufferSeconds,
    ) ?? 0,
  );
  const ttlSeconds = Math.max(
    0,
    normalizeNumber(macro.ttlSeconds ?? macro.remainingSeconds ?? macro.ttl ?? macro.timeLeft) ?? 0,
  );
  const title = normalizeString(macro.title ?? macro.label ?? macro.name) ?? titleForCtfMacro(role);
  const explicitStatus = normalizeString(macro.status ?? macro.message ?? macro.detail ?? macro.description ?? macro.text);
  const secondsText = returnWindowSeconds > 0 ? `${Math.ceil(returnWindowSeconds)}S` : standoffSeconds > 0 ? `${Math.ceil(standoffSeconds)}S` : null;
  const cue =
    normalizeString(macro.cue ?? macro.call ?? macro.summary) ??
    (secondsText
      ? routeLabel !== 'Route'
        ? `${secondsText} · ${routeLabel.toUpperCase()}`
        : secondsText
      : routeLabel !== 'Route'
        ? routeLabel.toUpperCase()
        : title);
  const status =
    explicitStatus ??
    (role === 'carrier'
      ? secondsText
        ? `Push out · ${secondsText}`
        : `Push out through ${routeLabel}`
      : role === 'chaser'
        ? secondsText
          ? `Cut off the return · ${secondsText}`
          : `Cut off the return at ${routeLabel}`
        : role === 'returner'
          ? secondsText
            ? `Close on the carrier · ${secondsText}`
            : `Close on the carrier at ${routeLabel}`
          : role === 'stand-defense'
            ? secondsText
              ? `Hold the stand · ${secondsText}`
              : `Hold the stand at ${routeLabel}`
            : role === 'standoff'
              ? secondsText
                ? `Keep spacing · ${secondsText}`
                : `Keep spacing at ${routeLabel}`
              : role === 'escort'
                ? secondsText
                  ? `Screen the carrier · ${secondsText}`
                  : `Screen the carrier at ${routeLabel}`
                : routeLabel !== 'Route'
                  ? `${title} · ${routeLabel.toUpperCase()}`
                  : title);
  const urgency = normalizeUrgency(macro.urgency ?? macro.priority, role);

  return {
    role,
    title,
    status,
    cue,
    tone: toneForCtfMacro(role, team),
    urgency,
    ttlSeconds: ttlSeconds > 0 ? ttlSeconds : null,
    team,
    flagTeam,
    routeZoneId,
    returnWindowSeconds: returnWindowSeconds > 0 ? returnWindowSeconds : null,
    standoffSeconds: standoffSeconds > 0 ? standoffSeconds : null,
    objectiveTelemetry: Array.isArray(macro.objectiveTelemetry)
      ? macro.objectiveTelemetry
          .map((entry) => {
            if (!entry || typeof entry !== 'object') return null;
            const telemetry = entry as Record<string, unknown>;
            const telemetryTeam = normalizeTeamId(telemetry.team);
            if (!telemetryTeam) return null;
            return {
              team: telemetryTeam,
              grabs: normalizeNumber(telemetry.grabs) ?? 0,
              returns: normalizeNumber(telemetry.returns) ?? 0,
              captures: normalizeNumber(telemetry.captures) ?? 0,
              carrierKills: normalizeNumber(telemetry.carrierKills) ?? 0,
              standoffs: normalizeNumber(telemetry.standoffs) ?? 0,
              generatorDestroys: normalizeNumber(telemetry.generatorDestroys) ?? 0,
              generatorRepairs: normalizeNumber(telemetry.generatorRepairs) ?? 0,
            };
          })
          .filter((entry): entry is NonNullable<typeof entry> => Boolean(entry))
      : undefined,
  };
}

function titleCase(value: string) {
  return sharedTitleCase(value);
}

function normalizePackSummary(value: unknown, fallbackSlot = 1): PackSummary {
  const pack =
    value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : typeof value === 'string'
        ? { label: value, type: value }
        : {};
  const slotCandidate = normalizeNumber(pack.packSlot ?? pack.selectedPackSlot ?? pack.selectedPackIndex ?? pack.slot);
  const normalizedSlot = slotCandidate == null ? fallbackSlot : Math.max(1, Math.round(slotCandidate));
  const cooldownSeconds = Math.max(
    0,
    normalizeNumber(pack.packCooldownSeconds ?? pack.packCooldown ?? pack.cooldownSeconds ?? pack.cooldown) ?? 0,
  );
  const charges = normalizeNumber(pack.packCharges ?? pack.charges);
  const maxCharges = normalizeNumber(pack.packMaxCharges ?? pack.maxCharges);
  const energy = normalizeNumber(pack.packEnergy ?? pack.energy);
  const id = normalizePackId(pack.packId ?? pack.id ?? pack.selectedPackId ?? pack.packType ?? pack.type);
  const type = normalizePackId(pack.packType ?? pack.type ?? pack.kind ?? pack.role);
  const rawLabel = normalizeString(pack.packLabel ?? pack.label ?? pack.name);
  const hasPackData =
    Boolean(rawLabel) ||
    Boolean(id) ||
    Boolean(type) ||
    slotCandidate != null ||
    cooldownSeconds > 0 ||
    charges != null ||
    maxCharges != null ||
    energy != null ||
    typeof pack.packState === 'string' ||
    typeof pack.state === 'string' ||
    'packActive' in pack ||
    'active' in pack ||
    'packReady' in pack ||
    'ready' in pack;
  const label = hasPackData ? rawLabel ?? (type ? titleCase(type) : id ? titleCase(id) : `Pack ${normalizedSlot}`) : null;
  const state = hasPackData
    ? typeof pack.packState === 'string' && pack.packState.length > 0
      ? pack.packState
      : typeof pack.state === 'string' && pack.state.length > 0
        ? pack.state
        : cooldownSeconds > 0
          ? 'cooldown'
          : 'ready'
    : null;
  const ready = hasPackData ? normalizeBoolean(pack.packReady ?? pack.ready ?? (state === 'ready' || cooldownSeconds <= 0)) : null;
  const active = hasPackData ? normalizeBoolean(pack.packActive ?? pack.active ?? ready) : null;

  return {
    slot: normalizedSlot,
    id,
    label,
    type,
    state,
    active,
    ready,
    cooldownSeconds,
    charges: charges == null ? null : Math.max(0, charges),
    maxCharges: maxCharges == null ? null : Math.max(0, maxCharges),
    energy: energy == null ? null : Math.max(0, energy),
  };
}

function normalizePackOptions(value: unknown): PackSummary[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => normalizePackSummary(entry, index + 1))
    .filter((entry) => Boolean(entry.label));
}

function normalizeGrenadeSummary(value: unknown, fallbackSlot = 1): GrenadeSummary {
  const grenade =
    value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : typeof value === 'string'
        ? { label: value, type: value }
        : {};
  const slotCandidate = normalizeNumber(
    grenade.grenadeSlot ?? grenade.selectedGrenadeSlot ?? grenade.selectedGrenadeIndex ?? grenade.slot,
  );
  const normalizedSlot = slotCandidate == null ? fallbackSlot : Math.max(1, Math.round(slotCandidate));
  const cooldownSeconds = Math.max(
    0,
    normalizeNumber(
      grenade.grenadeCooldownSeconds ?? grenade.cooldownSeconds ?? grenade.grenadeCooldown ?? grenade.cooldown,
    ) ?? 0,
  );
  const charges = normalizeNumber(grenade.grenadeCharges ?? grenade.charges);
  const maxCharges = normalizeNumber(grenade.grenadeMaxCharges ?? grenade.maxCharges);
  const id = normalizeString(
    grenade.grenadeId ?? grenade.id ?? grenade.selectedGrenadeId ?? grenade.grenadeType ?? grenade.type ?? grenade.kind,
  );
  const type = normalizeString(grenade.grenadeType ?? grenade.type ?? grenade.kind ?? grenade.role ?? id);
  const rawLabel = normalizeString(grenade.grenadeLabel ?? grenade.label ?? grenade.name);
  const hasGrenadeData =
    Boolean(rawLabel) ||
    Boolean(id) ||
    Boolean(type) ||
    slotCandidate != null ||
    cooldownSeconds > 0 ||
    charges != null ||
    maxCharges != null ||
    typeof grenade.grenadeState === 'string' ||
    typeof grenade.state === 'string' ||
    'grenadeActive' in grenade ||
    'active' in grenade ||
    'grenadeReady' in grenade ||
    'ready' in grenade;
  const label = hasGrenadeData
    ? rawLabel ?? (type ? titleCase(type) : id ? titleCase(id) : `Grenade ${normalizedSlot}`)
    : null;
  const state = hasGrenadeData
    ? typeof grenade.grenadeState === 'string' && grenade.grenadeState.length > 0
      ? grenade.grenadeState
      : typeof grenade.state === 'string' && grenade.state.length > 0
        ? grenade.state
        : cooldownSeconds > 0
          ? 'cooldown'
          : 'ready'
    : null;
  const ready = hasGrenadeData
    ? normalizeBoolean(grenade.grenadeReady ?? grenade.ready ?? (state === 'ready' || cooldownSeconds <= 0))
    : null;
  const active = hasGrenadeData ? normalizeBoolean(grenade.grenadeActive ?? grenade.active ?? ready) : null;

  return {
    slot: normalizedSlot,
    id,
    label,
    type,
    state,
    active,
    ready,
    cooldownSeconds,
    charges: charges == null ? null : Math.max(0, charges),
    maxCharges: maxCharges == null ? null : Math.max(0, maxCharges),
  };
}

function normalizeGrenadeOptions(value: unknown): GrenadeSummary[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => normalizeGrenadeSummary(entry, index + 1))
    .filter((entry) => Boolean(entry.label));
}

function normalizeVec3(value: unknown): { x: number; y: number; z: number } | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const vector = value as Record<string, unknown>;
  const x = normalizeNumber(vector.x);
  const y = normalizeNumber(vector.y);
  const z = normalizeNumber(vector.z);

  if (x == null || y == null || z == null) {
    return null;
  }

  return { x, y, z };
}

function packSourceFromValue(value: unknown) {
  if (value && typeof value === 'object') {
    return { ...(value as Record<string, unknown>) };
  }

  if (typeof value === 'string') {
    return {
      packId: value,
      packType: value,
    };
  }

  return {};
}

function grenadeSourceFromValue(value: unknown) {
  if (value && typeof value === 'object') {
    return { ...(value as Record<string, unknown>) };
  }

  if (typeof value === 'string') {
    return {
      grenadeId: value,
      grenadeType: value,
    };
  }

  return {};
}

function normalizeDirective(value: unknown, fallbackTeam: TeamId = null): TeamDirective | null {
  if (Array.isArray(value)) {
    for (const entry of value) {
      const normalized = normalizeDirective(entry, fallbackTeam);
      if (normalized) {
        return normalized;
      }
    }
    return null;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const directive = value as Record<string, unknown>;
  const team = normalizeTeamId(directive.team ?? directive.targetTeam ?? directive.issuingTeam ?? fallbackTeam);
  const call = normalizeString(
    directive.call ??
      directive.command ??
      directive.commandRequest ??
      directive.type ??
      directive.directiveType ??
      directive.id,
  );
  const routeZoneId = normalizeString(directive.routeZoneId ?? directive.routeZone ?? directive.route);
  const callSummary = summarizeDirectiveCall(call, routeZoneId);
  const message =
    normalizeString(directive.message ?? directive.text ?? directive.label) ??
    callSummary;
  const ttlSeconds = Math.max(
    0,
    normalizeNumber(directive.ttlSeconds ?? directive.remainingSeconds ?? directive.ttl ?? directive.timeLeft) ?? 0,
  );

  if (!message && !call && ttlSeconds <= 0) {
    return null;
  }

  return {
    id: normalizeString(directive.id),
    team,
    call,
    message: message ?? 'Directive active',
    ttlSeconds,
    sourcePlayerId: normalizeString(directive.sourcePlayerId ?? directive.callerId ?? directive.issuerId ?? directive.playerId),
    sourceName: normalizeString(directive.sourceName ?? directive.callerName ?? directive.issuerName ?? directive.playerName),
  };
}

function normalizeTacticalPing(value: unknown, index: number): TacticalPing | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const ping = value as Record<string, unknown>;
  const position =
    normalizeVec3(ping.position) ??
    normalizeVec3({
      x: ping.x,
      y: ping.y ?? 0,
      z: ping.z,
    });
  if (!position) {
    return null;
  }

  return {
    id: normalizeString(ping.id) ?? `ping-${index}`,
    team: normalizeTeamId(ping.team ?? ping.sourceTeam ?? ping.playerTeam),
    position,
    ttlSeconds: Math.max(
      0,
      normalizeNumber(ping.ttlSeconds ?? ping.remainingSeconds ?? ping.ttl ?? ping.timeLeft) ?? 0,
    ),
    kind: normalizeString(ping.kind ?? ping.type),
    label: normalizeString(ping.label ?? ping.message),
    sourcePlayerId: normalizeString(ping.sourcePlayerId ?? ping.playerId ?? ping.issuerId),
  };
}

function normalizePingWaypoint(value: unknown, index: number): PingWaypoint | null {
  if (!value || typeof value !== 'object') return null;
  const wp = value as Record<string, unknown>;
  const position = normalizeVec3(wp.position);
  if (!position) return null;
  const kindRaw = normalizeString(wp.kind);
  const kind: PingWaypoint['kind'] = kindRaw === 'ping' || kindRaw === 'repair-needed' ? kindRaw : null;
  return {
    id: normalizeString(wp.id) ?? `waypoint-${index}`,
    team: normalizeTeamId(wp.team),
    position,
    ttlSeconds: Math.max(0, normalizeNumber(wp.ttlSeconds) ?? 0),
    callerId: normalizeString(wp.callerId),
    callerName: normalizeString(wp.callerName),
    kind,
    color: normalizeString(wp.color),
  };
}

function normalizePingWaypoints(value: unknown): PingWaypoint[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((wp, index) => normalizePingWaypoint(wp, index))
    .filter((wp): wp is PingWaypoint => Boolean(wp));
}

function normalizeTacticalPings(value: unknown): TacticalPing[] {
  if (Array.isArray(value)) {
    return value
      .map((ping, index) => normalizeTacticalPing(ping, index))
      .filter((ping): ping is TacticalPing => Boolean(ping));
  }

  const singlePing = normalizeTacticalPing(value, 0);
  return singlePing ? [singlePing] : [];
}

function normalizeRouteCaptureZone(value: unknown, index: number): RouteCaptureZone | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const zone = value as Record<string, unknown>;
  const position = normalizeVec3(zone.position);
  if (!position) {
    return null;
  }

  return {
    id: normalizeString(zone.id) ?? `route-zone-${index}`,
    position,
    radius: Math.max(1, normalizeNumber(zone.radius) ?? 10),
    ownerTeam: normalizeTeamId(zone.ownerTeam),
    captureTargetTeam: normalizeTeamId(zone.captureTargetTeam),
    captureProgress: Math.min(1, Math.max(0, normalizeNumber(zone.captureProgress) ?? 0)),
    contested: normalizeBoolean(zone.contested),
    carrierInside: normalizeBoolean(zone.carrierInside),
    scoringMultiplier: Math.max(1, normalizeNumber(zone.scoringMultiplier) ?? 1),
  };
}

function normalizeRouteCaptureZones(value: unknown): RouteCaptureZone[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((zone, index) => normalizeRouteCaptureZone(zone, index))
    .filter((zone): zone is RouteCaptureZone => Boolean(zone));
}

function normalizeObjectiveScoringHint(value: unknown, index: number): ObjectiveScoringHint | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const hint = value as Record<string, unknown>;
  const message = normalizeString(hint.message);
  if (!message) {
    return null;
  }

  return {
    id: normalizeString(hint.id) ?? `objective-hint-${index}`,
    team: normalizeTeamId(hint.team),
    kind: normalizeString(hint.kind) ?? 'base',
    message,
    points: normalizeNumber(hint.points) ?? 0,
    multiplier: Math.max(0, normalizeNumber(hint.multiplier) ?? 1),
    ttlSeconds: Math.max(0, normalizeNumber(hint.ttlSeconds ?? hint.remainingSeconds ?? hint.ttl) ?? 0),
    playerId: normalizeString(hint.playerId),
    flagTeam: normalizeTeamId(hint.flagTeam),
    routeZoneId: normalizeString(hint.routeZoneId),
  };
}

function normalizeObjectiveScoringHints(value: unknown): ObjectiveScoringHint[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((hint, index) => normalizeObjectiveScoringHint(hint, index))
    .filter((hint): hint is ObjectiveScoringHint => Boolean(hint));
}

function normalizeBaseFlagInteractionHook(value: unknown, index: number): BaseFlagInteractionHook | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const hook = value as Record<string, unknown>;
  const position = normalizeVec3(hook.position);
  const detail = normalizeString(hook.detail);
  if (!position || !detail) {
    return null;
  }

  const statusCandidate = normalizeString(hook.status);
  const status = statusCandidate === 'warning' || statusCandidate === 'critical' ? statusCandidate : 'stable';

  return {
    id: normalizeString(hook.id) ?? `base-hook-${index}`,
    team: normalizeTeamId(hook.team),
    flagTeam: normalizeTeamId(hook.flagTeam),
    kind: normalizeString(hook.kind) ?? 'base',
    position,
    active: normalizeBoolean(hook.active),
    intensity: Math.min(1, Math.max(0, normalizeNumber(hook.intensity) ?? 0)),
    status,
    detail,
    relatedEntityId: normalizeString(hook.relatedEntityId),
  };
}

function normalizeBaseFlagInteractionHooks(value: unknown): BaseFlagInteractionHook[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((hook, index) => normalizeBaseFlagInteractionHook(hook, index))
    .filter((hook): hook is BaseFlagInteractionHook => Boolean(hook));
}

type RichPlayerSnapshot = PlayerSnapshot & {
  team: TeamId;
  armorClass: ArmorClass;
  selectedWeapon: WeaponId;
  weaponHeat: number;
  weaponCooldown: number;
  overheated: boolean;
  spawnProtected: boolean;
  carryingFlagTeam: TeamId;
  pack?: unknown;
  packState?: unknown;
  packSnapshot?: unknown;
  packs?: unknown;
  packOptions?: unknown;
  packSlot?: unknown;
  selectedPack?: unknown;
  selectedPackSlot?: unknown;
  selectedPackIndex?: unknown;
  packId?: unknown;
  packType?: unknown;
  packLabel?: unknown;
  packCooldown?: unknown;
  packCooldownSeconds?: unknown;
  packCharges?: unknown;
  packMaxCharges?: unknown;
  packEnergy?: unknown;
  packReady?: unknown;
  packActive?: unknown;
  grenade?: unknown;
  grenadeState?: unknown;
  grenadeSnapshot?: unknown;
  grenades?: unknown;
  grenadeOptions?: unknown;
  grenadeSlot?: unknown;
  selectedGrenade?: unknown;
  selectedGrenadeSlot?: unknown;
  selectedGrenadeIndex?: unknown;
  grenadeId?: unknown;
  grenadeType?: unknown;
  grenadeLabel?: unknown;
  grenadeCooldown?: unknown;
  grenadeCooldownSeconds?: unknown;
  grenadeCharges?: unknown;
  grenadeMaxCharges?: unknown;
  grenadeReady?: unknown;
  grenadeActive?: unknown;
  mineCharges?: number;
  mineCooldown?: number;
};

type RichMatchSnapshot = MatchSnapshot & {
  phase: string;
  teamScores: Record<string, number>;
  winnerTeam: TeamId;
  countdownSeconds: number;
  directive?: unknown;
  activeDirective?: unknown;
  teamDirective?: unknown;
  pings?: unknown;
  tacticalPings?: unknown;
  ctfMacro?: unknown;
  awards?: unknown;
};

export type RichWorldSnapshot = WorldSnapshot & {
  mapId?: MapId;
  players: RichPlayerSnapshot[];
  scoreboardPlayers?: PlayerScoreboardSnapshot[];
  match: RichMatchSnapshot;
  flags: SnapshotEntity[];
  boostPads: SnapshotEntity[];
  generators: SnapshotEntity[];
  sensors: SnapshotEntity[];
  shieldDoors: SnapshotEntity[];
  inventoryStations: SnapshotEntity[];
  repairZones: SnapshotEntity[];
  turrets: SnapshotEntity[];
  effects: SnapshotEntity[];
  vehicles?: unknown;
  deployables?: unknown;
  packs: SnapshotEntity[];
  directive?: unknown;
  activeDirective?: unknown;
  teamDirective?: unknown;
  directives?: unknown;
  pings?: unknown;
  tacticalPings?: unknown;
  routeCaptureZones?: unknown;
  objectiveScoringHints?: unknown;
  baseFlagInteractionHooks?: unknown;
  ctfMacro?: unknown;
};

export type ClientPlayerState = PlayerState & {
  team: TeamId;
  armorClass: ArmorClass;
  selectedWeapon: WeaponId;
  ads: boolean;
  weaponHeat: number;
  weaponCooldown: number;
  overheated: boolean;
  spawnProtected: boolean;
  carryingFlagTeam: TeamId;
  packSlot: number | null;
  packId: string | null;
  packLabel: string | null;
  packType: string | null;
  packState: string | null;
  packActive: boolean | null;
  packReady: boolean | null;
  packCooldownSeconds: number | null;
  packCharges: number | null;
  packMaxCharges: number | null;
  packEnergy: number | null;
  packOptions: PackSummary[];
  grenadeSlot?: number | null;
  grenadeId?: string | null;
  grenadeLabel?: string | null;
  grenadeType?: string | null;
  grenadeState?: string | null;
  grenadeActive?: boolean | null;
  grenadeReady?: boolean | null;
  grenadeCooldownSeconds?: number | null;
  grenadeCharges?: number | null;
  grenadeMaxCharges?: number | null;
  grenadeOptions?: GrenadeSummary[];
  mineCharges?: number | null;
  mineMaxCharges?: number | null;
  mineCooldown?: number | null;
  botRole?: string;
  botIntent?: BotIntentSnapshot;
  weaponDebug?: WeaponDebugTelemetrySnapshot;
};

export type RemotePlayer = ClientPlayerState;
export type MatchState = RichMatchSnapshot;
export type WorldEffect = SnapshotEntity;

export type ScoreboardPlayerState = {
  id: string;
  displayName: string;
  team: TeamId;
  score: number;
  kills: number;
  deaths: number;
  captures: number;
  returns: number;
  isBot: boolean;
};

export type MatchAwardState = MatchAwardSnapshot;

type ConnectionState = 'connecting' | 'online' | 'reconnecting' | 'offline' | 'standalone';
export type GamePhase = 'menu' | 'playing' | 'paused' | 'postmatch';

export type Settings = {
  masterVolume: number;
  musicVolume: number;
  sensitivity: number;
  fov: number;
  graphicsQuality: 'low' | 'medium' | 'high';
};

const DEFAULT_SETTINGS: Settings = {
  masterVolume: 70,
  musicVolume: 50,
  sensitivity: 1.0,
  fov: 90,
  graphicsQuality: 'medium',
};

function loadSettings(): Settings {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('tribes2-settings') : null;
    if (raw) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
    }
  } catch {
    // ignore
  }
  return { ...DEFAULT_SETTINGS };
}

function persistSettings(settings: Settings): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('tribes2-settings', JSON.stringify(settings));
    }
  } catch {
    // ignore
  }
}

export type FloatingDamage = {
  id: string;
  amount: number;
  x: number;
  y: number;
  age: number;
  critical: boolean;
};

export type SmokeStateSnapshot = {
  gamePhase: GamePhase;
  connectionState: ConnectionState;
  connected: boolean;
  localPosition: { x: number; y: number; z: number };
  serverTick: number;
  eventCount: number;
  effectCount: number;
  mapId: MapId;
};

type GameStore = {
  connected: boolean;
  connectionState: ConnectionState;
  mapId: MapId;
  localId: string | null;
  localPlayer: ClientPlayerState;
  remotePlayers: Record<string, RemotePlayer>;
  scoreboardPlayers: ScoreboardPlayerState[];
  projectiles: ProjectileSnapshot[];
  serverTick: number;
  lastAppliedServerTick: number;
  predictionBlendWeight: number;
  match: MatchState;
  flags: WorldEffect[];
  boostPads: WorldEffect[];
  generators: WorldEffect[];
  sensors: WorldEffect[];
  shieldDoors: WorldEffect[];
  inventoryStations: WorldEffect[];
  repairZones: WorldEffect[];
  turrets: WorldEffect[];
  effects: WorldEffect[];
  vehicles: WorldEffect[];
  deployables: WorldEffect[];
  packs: WorldEffect[];
  activeDirective: TeamDirective | null;
  ctfMacro: CtfMacroSummary | null;
  tacticalPings: TacticalPing[];
  pingWaypoints: PingWaypoint[];
  routeCaptureZones: RouteCaptureZone[];
  objectiveScoringHints: ObjectiveScoringHint[];
  baseFlagInteractionHooks: BaseFlagInteractionHook[];
  events: MatchEvent[];
  floatingDamages: FloatingDamage[];
  killStreak: number;
  lastKillTime: number;
  lastHitDirection: number | null;
  lastHitTime: number;
  gamePhase: GamePhase;
  siegeState: SiegeGameState | null;
  chatMessages: ChatMessage[];
  chatOpen: boolean;
  setSiegeState: (state: SiegeGameState | null) => void;
  addChatMessage: (msg: ChatMessage) => void;
  setChatOpen: (open: boolean) => void;
  setGamePhase: (phase: GamePhase) => void;
  addFloatingDamage: (dmg: FloatingDamage) => void;
  tickFloatingDamages: (dt: number) => void;
  recordLocalKill: (timestamp: number) => void;
  resetMatch: () => void;
  setConnected: (connected: boolean) => void;
  setConnectionState: (connectionState: ConnectionState) => void;
  startStandaloneSession: (options?: StandaloneSessionOptions) => void;
  setWelcome: (id: string) => void;
  setLocalPlayer: (
    player: PlayerState &
      Partial<
        Pick<
          ClientPlayerState,
          | 'ads'
          | 'packSlot'
          | 'packId'
          | 'packLabel'
          | 'packType'
          | 'packState'
          | 'packActive'
          | 'packReady'
          | 'packCooldownSeconds'
          | 'packCharges'
          | 'packMaxCharges'
          | 'packEnergy'
          | 'packOptions'
          | 'grenadeSlot'
          | 'grenadeId'
          | 'grenadeLabel'
          | 'grenadeType'
          | 'grenadeState'
          | 'grenadeActive'
          | 'grenadeReady'
          | 'grenadeCooldownSeconds'
          | 'grenadeCharges'
           | 'grenadeMaxCharges'
           | 'grenadeOptions'
           | 'weaponDebug'
        >
      >,
  ) => void;
  applySnapshot: (snapshot: RichWorldSnapshot, predictionBlendWeightOverride?: number) => void;
  pendingArmorClass: SharedArmorClass | null;
  pendingWeaponSlots: string[];
  setPendingLoadout: (armorClass: SharedArmorClass, weapons: string[]) => void;
  clearPendingLoadout: () => void;
  lastKillEvent: KillEvent | null;
  killReplayVisible: boolean;
  multiKillCount: number;
  setKillEvent: (event: KillEvent) => void;
  clearKillReplay: () => void;
  settings: Settings;
  updateSettings: (partial: Partial<Settings>) => void;
};

const DEFAULT_MATCH: MatchState = {
  remainingSeconds: 180,
  scoreToWin: 15,
  leaderName: 'No leader',
  phase: 'warmup',
  teamScores: { red: 0, blue: 0 },
  winnerTeam: null,
  countdownSeconds: 180,
  awards: [],
};

function normalizeAwards(value: unknown): MatchAwardState[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const award = entry as Record<string, unknown>;
      const kind = normalizeString(award.kind);
      if (
        kind !== 'mvp' &&
        kind !== 'top-capper' &&
        kind !== 'best-defender' &&
        kind !== 'generator-breaker' &&
        kind !== 'route-runner' &&
        kind !== 'vehicle-ace'
      ) {
        return null;
      }
      const playerId = normalizeString(award.playerId);
      const playerName = normalizeString(award.playerName);
      if (!playerId || !playerName) return null;
      const normalizedAward: MatchAwardState = {
        kind,
        label: normalizeString(award.label) ?? kind,
        playerId,
        playerName,
        team: normalizeTeamId(award.team) ?? null,
        value: normalizeNumber(award.value) ?? 0,
      };
      return normalizedAward;
    })
    .filter((award): award is MatchAwardState => award !== null);
}

function blendScalar(current: number, next: number, currentWeight = 0.7) {
  return current * currentWeight + next * (1 - currentWeight);
}

function blendVector<T extends { x: number; y: number; z: number }>(current: T, next: T, currentWeight = 0.7) {
  return {
    x: blendScalar(current.x, next.x, currentWeight),
    y: blendScalar(current.y, next.y, currentWeight),
    z: blendScalar(current.z, next.z, currentWeight),
  };
}

function isDeepEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }

  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) {
    return false;
  }

  if (Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }

  if (Array.isArray(a)) {
    if ((a as unknown[]).length !== (b as unknown[]).length) {
      return false;
    }

    return (a as unknown[]).every((value, index) => isDeepEqual(value, (b as unknown[])[index]));
  }

  const objA = a as Record<string, unknown>;
  const objB = b as Record<string, unknown>;

  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);

  if (keysA.length !== keysB.length) {
    return false;
  }

  return keysA.every((key) => isDeepEqual(objA[key], objB[key]));
}

function isClientPlayerStateEqual(a: ClientPlayerState, b: ClientPlayerState) {
  return isDeepEqual(a, b);
}

function isClientPlayerStateCoreEqual(a: ClientPlayerState, b: ClientPlayerState) {
  const { weaponDebug: _weaponDebugA, ...restA } = a;
  const { weaponDebug: _weaponDebugB, ...restB } = b;
  return isDeepEqual(restA, restB);
}

function shouldSyncWeaponDebug(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return Boolean(window.tribes2DebugOverlay || window.tribes2DebugReplay);
}

function normalizeWorldObjects(value: unknown): WorldEffect[] {
  return Array.isArray(value) ? (value as WorldEffect[]) : [];
}

function normalizeSensorSnapshots(value: unknown): SensorSnapshot[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const sensor = entry as Record<string, unknown>;
      const position = normalizeVec3(sensor.position);
      if (!position) {
        return null;
      }

      const health = normalizeNumber(sensor.health);
      const maxHealth = normalizeNumber(sensor.maxHealth);

      return {
        id: normalizeString(sensor.id) ?? `sensor-${index}`,
        team: normalizeTeamId(sensor.team),
        position,
        radius: Math.max(1, normalizeNumber(sensor.radius) ?? 5),
        active: normalizeBoolean(sensor.active),
        health: Math.max(0, health ?? 0),
        maxHealth: Math.max(1, maxHealth ?? health ?? 180),
        respawnSeconds: Math.max(0, normalizeNumber(sensor.respawnSeconds) ?? 0),
      };
    })
    .filter((entry): entry is SensorSnapshot => Boolean(entry));
}

function normalizeShieldDoorSnapshots(value: unknown): ShieldDoorSnapshot[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry, index) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const door = entry as Record<string, unknown>;
      const position = normalizeVec3(door.position);
      if (!position) {
        return null;
      }

      const active = normalizeBoolean(door.active);
      const powered = door.powered == null ? active : normalizeBoolean(door.powered);

      return {
        id: normalizeString(door.id) ?? `shield-door-${index}`,
        team: normalizeTeamId(door.team),
        position,
        radius: Math.max(1, normalizeNumber(door.radius) ?? 7),
        active,
        powered,
      };
    })
    .filter(Boolean) as ShieldDoorSnapshot[];
}

function normalizeTeamScores(value: unknown): Record<string, number> {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>).flatMap(([team, score]) => {
      if (typeof score !== 'number' || Number.isNaN(score)) {
        return [];
      }

      return [[team, score]];
    }),
  );
}

function toClientPlayerState(snapshot: RichPlayerSnapshot): ClientPlayerState {
  const packId = normalizePackId(snapshot.packId ?? snapshot.selectedPack ?? snapshot.packType);
  const inferredPackSlot = packId ? PACK_IDS.indexOf(packId) + 1 : null;
  const packSource = {
    ...packSourceFromValue(snapshot.packSnapshot ?? snapshot.pack ?? snapshot.selectedPack ?? snapshot.packType ?? snapshot.packId),
    ...(snapshot.packSlot != null || snapshot.selectedPackSlot != null || snapshot.selectedPackIndex != null || inferredPackSlot != null
      ? { packSlot: snapshot.packSlot ?? snapshot.selectedPackSlot ?? snapshot.selectedPackIndex ?? inferredPackSlot }
      : {}),
    ...(snapshot.packId != null || snapshot.selectedPack != null || snapshot.packType != null || packId != null
      ? { packId: snapshot.packId ?? snapshot.selectedPack ?? snapshot.packType ?? packId }
      : {}),
    ...(snapshot.packType != null || snapshot.packId != null || snapshot.selectedPack != null || packId != null
      ? { packType: snapshot.packType ?? snapshot.packId ?? snapshot.selectedPack ?? packId }
      : {}),
    ...(snapshot.packLabel != null ? { packLabel: snapshot.packLabel } : {}),
    ...(snapshot.packState != null ? { packState: snapshot.packState } : {}),
    ...(snapshot.packCooldownSeconds != null || snapshot.packCooldown != null
      ? { packCooldownSeconds: snapshot.packCooldownSeconds ?? snapshot.packCooldown }
      : {}),
    ...(snapshot.packCharges != null ? { packCharges: snapshot.packCharges } : {}),
    ...(snapshot.packMaxCharges != null ? { packMaxCharges: snapshot.packMaxCharges } : {}),
    ...(snapshot.packEnergy != null ? { packEnergy: snapshot.packEnergy } : {}),
    ...(snapshot.packReady != null ? { packReady: snapshot.packReady } : {}),
    ...(snapshot.packActive != null || snapshot.packActiveSeconds != null
      ? { packActive: snapshot.packActive ?? ((normalizeNumber(snapshot.packActiveSeconds) ?? 0) > 0) }
      : {}),
  };
  const packOptions = normalizePackOptions(snapshot.packs ?? snapshot.packOptions);
  const packHasData =
    Boolean(packOptions.length) ||
    snapshot.pack != null ||
    snapshot.packState != null ||
    snapshot.packSnapshot != null ||
    snapshot.selectedPack != null ||
    snapshot.packType != null ||
    snapshot.packId != null ||
    snapshot.packLabel != null ||
    snapshot.packCooldown != null ||
    snapshot.packCooldownSeconds != null ||
    snapshot.packCharges != null ||
    snapshot.packMaxCharges != null ||
    snapshot.packEnergy != null ||
    snapshot.packReady != null ||
    snapshot.packActive != null ||
    snapshot.packSlot != null ||
    snapshot.selectedPackSlot != null ||
    snapshot.selectedPackIndex != null;
  const resolvedPack = packHasData
    ? normalizePackSummary(packSource, normalizeNumber(snapshot.packSlot ?? snapshot.selectedPackSlot ?? snapshot.selectedPackIndex ?? 1) ?? 1)
    : {
        slot: null,
        id: null,
        label: null,
        type: null,
        state: null,
        active: null,
        ready: null,
        cooldownSeconds: null,
        charges: null,
        maxCharges: null,
        energy: null,
      };
  const grenadeIdCandidate = normalizeString(snapshot.grenadeId ?? snapshot.selectedGrenade ?? snapshot.grenadeType);
  const inferredGrenadeSlot =
    grenadeIdCandidate && GRENADE_IDS.includes(grenadeIdCandidate as (typeof GRENADE_IDS)[number])
      ? GRENADE_IDS.indexOf(grenadeIdCandidate as (typeof GRENADE_IDS)[number]) + 1
      : null;
  const grenadeSource = {
    ...grenadeSourceFromValue(
      snapshot.grenadeSnapshot ?? snapshot.grenade ?? snapshot.selectedGrenade ?? snapshot.grenadeType ?? snapshot.grenadeId,
    ),
    ...(snapshot.grenadeSlot != null || snapshot.selectedGrenadeSlot != null || snapshot.selectedGrenadeIndex != null || inferredGrenadeSlot != null
      ? { grenadeSlot: snapshot.grenadeSlot ?? snapshot.selectedGrenadeSlot ?? snapshot.selectedGrenadeIndex ?? inferredGrenadeSlot }
      : {}),
    ...(snapshot.grenadeId != null || snapshot.selectedGrenade != null || snapshot.grenadeType != null || grenadeIdCandidate != null
      ? { grenadeId: snapshot.grenadeId ?? snapshot.selectedGrenade ?? snapshot.grenadeType ?? grenadeIdCandidate }
      : {}),
    ...(snapshot.grenadeType != null || snapshot.grenadeId != null || snapshot.selectedGrenade != null || grenadeIdCandidate != null
      ? { grenadeType: snapshot.grenadeType ?? snapshot.grenadeId ?? snapshot.selectedGrenade ?? grenadeIdCandidate }
      : {}),
    ...(snapshot.grenadeLabel != null ? { grenadeLabel: snapshot.grenadeLabel } : {}),
    ...(snapshot.grenadeState != null ? { grenadeState: snapshot.grenadeState } : {}),
    ...(snapshot.grenadeCooldownSeconds != null || snapshot.grenadeCooldown != null
      ? { grenadeCooldownSeconds: snapshot.grenadeCooldownSeconds ?? snapshot.grenadeCooldown }
      : {}),
    ...(snapshot.grenadeCharges != null ? { grenadeCharges: snapshot.grenadeCharges } : {}),
    ...(snapshot.grenadeMaxCharges != null || grenadeIdCandidate != null
      ? { grenadeMaxCharges: snapshot.grenadeMaxCharges ?? GRENADE.maxCharges }
      : {}),
    ...(snapshot.grenadeReady != null ? { grenadeReady: snapshot.grenadeReady } : {}),
    ...(snapshot.grenadeActive != null ? { grenadeActive: snapshot.grenadeActive } : {}),
  };
  const grenadeOptions = normalizeGrenadeOptions(snapshot.grenades ?? snapshot.grenadeOptions);
  const grenadeHasData =
    Boolean(grenadeOptions.length) ||
    snapshot.grenade != null ||
    snapshot.grenadeState != null ||
    snapshot.grenadeSnapshot != null ||
    snapshot.selectedGrenade != null ||
    snapshot.grenadeType != null ||
    snapshot.grenadeId != null ||
    snapshot.grenadeLabel != null ||
    snapshot.grenadeCooldown != null ||
    snapshot.grenadeCooldownSeconds != null ||
    snapshot.grenadeCharges != null ||
    snapshot.grenadeMaxCharges != null ||
    snapshot.grenadeReady != null ||
    snapshot.grenadeActive != null ||
    snapshot.grenadeSlot != null ||
    snapshot.selectedGrenadeSlot != null ||
    snapshot.selectedGrenadeIndex != null;
  const resolvedGrenade = grenadeHasData
    ? normalizeGrenadeSummary(
        grenadeSource,
        normalizeNumber(snapshot.grenadeSlot ?? snapshot.selectedGrenadeSlot ?? snapshot.selectedGrenadeIndex ?? inferredGrenadeSlot ?? 1) ?? 1,
      )
    : {
        slot: null,
        id: null,
        label: null,
        type: null,
        state: null,
        active: null,
        ready: null,
        cooldownSeconds: null,
        charges: null,
        maxCharges: null,
      };

  return {
    id: snapshot.id,
    position: snapshot.position,
    velocity: snapshot.velocity,
    yaw: snapshot.yaw,
    pitch: snapshot.pitch,
    energy: snapshot.energy,
    health: snapshot.health,
    skiing: false,
    isGrounded: false,
    skiCoyoteTimer: 0,
    speed: snapshot.speed,
    score: snapshot.score,
    respawnTimer: snapshot.respawnTimer,
    isBot: snapshot.isBot,
    displayName: snapshot.displayName,
    team: normalizeTeamId(snapshot.team),
    armorClass: normalizeArmorClass(snapshot.armorClass),
    selectedWeapon: normalizeWeaponId(snapshot.selectedWeapon),
    ads: false,
    weaponHeat: snapshot.weaponHeat ?? 0,
    weaponCooldown: snapshot.weaponCooldown ?? 0,
    overheated: snapshot.overheated ?? false,
    spawnProtected: snapshot.spawnProtected ?? false,
    carryingFlagTeam: normalizeTeamId(snapshot.carryingFlagTeam),
    kills: normalizeNumber(snapshot.kills) ?? 0,
    deaths: normalizeNumber(snapshot.deaths) ?? 0,
    captures: normalizeNumber(snapshot.captures) ?? 0,
    returns: normalizeNumber(snapshot.returns) ?? 0,
    packSlot: resolvedPack.slot,
    packId: resolvedPack.id,
    packLabel: resolvedPack.label,
    packType: resolvedPack.type,
    packState: resolvedPack.state,
    packActive: resolvedPack.active,
    packReady: resolvedPack.ready,
    packCooldownSeconds: resolvedPack.cooldownSeconds,
    packCharges: resolvedPack.charges,
    packMaxCharges: resolvedPack.maxCharges,
    packEnergy: resolvedPack.energy,
    packOptions: packHasData ? (packOptions.length ? packOptions : [resolvedPack]) : [],
    grenadeSlot: resolvedGrenade.slot,
    grenadeId: resolvedGrenade.id,
    grenadeLabel: resolvedGrenade.label,
    grenadeType: resolvedGrenade.type,
    grenadeState: resolvedGrenade.state,
    grenadeActive: resolvedGrenade.active,
    grenadeReady: resolvedGrenade.ready,
    grenadeCooldownSeconds: resolvedGrenade.cooldownSeconds,
    grenadeCharges: resolvedGrenade.charges,
    grenadeMaxCharges: resolvedGrenade.maxCharges,
    grenadeOptions: grenadeHasData ? (grenadeOptions.length ? grenadeOptions : [resolvedGrenade]) : [],
    mineCharges: normalizeNumber(snapshot.mineCharges),
    mineMaxCharges: 3,
    mineCooldown: normalizeNumber(snapshot.mineCooldown),
    botRole: snapshot.isBot ? (snapshot.botRole ?? undefined) : undefined,
    botIntent: snapshot.isBot ? snapshot.botIntent : undefined,
    weaponDebug: snapshot.weaponDebug,
  };
}

function resolveLocalPlayer(current: ClientPlayerState, snapshot: RichPlayerSnapshot, blendWeight: number): ClientPlayerState {
  const authoritative = toClientPlayerState(snapshot);
  const preserveCurrentPack = authoritative.packId == null && current.packId != null;

  return {
    ...current,
    ...authoritative,
    position: blendVector(current.position, authoritative.position, blendWeight),
    velocity: blendVector(current.velocity, authoritative.velocity, blendWeight * 0.87),
    yaw: authoritative.yaw,
    pitch: authoritative.pitch,
    skiing: current.skiing,
    isGrounded: current.isGrounded,
    speed: blendScalar(current.speed, authoritative.speed, 0.72),
    energy: authoritative.energy,
    health: authoritative.health,
    score: authoritative.score,
    respawnTimer: authoritative.respawnTimer,
    team: authoritative.team,
    ads: current.ads,
    weaponHeat: authoritative.weaponHeat,
    weaponCooldown: authoritative.weaponCooldown,
    overheated: authoritative.overheated,
    spawnProtected: authoritative.spawnProtected,
    carryingFlagTeam: authoritative.carryingFlagTeam,
    armorClass: authoritative.armorClass,
    selectedWeapon: authoritative.selectedWeapon ?? current.selectedWeapon,
    packSlot: preserveCurrentPack ? current.packSlot : (authoritative.packSlot ?? current.packSlot),
    packId: authoritative.packId ?? current.packId,
    packLabel: preserveCurrentPack ? current.packLabel : (authoritative.packLabel ?? current.packLabel),
    packType: preserveCurrentPack ? current.packType : (authoritative.packType ?? current.packType),
    packState: preserveCurrentPack ? current.packState : (authoritative.packState ?? current.packState),
    packActive: preserveCurrentPack ? current.packActive : (authoritative.packActive ?? current.packActive),
    packReady: preserveCurrentPack ? current.packReady : (authoritative.packReady ?? current.packReady),
    packCooldownSeconds: preserveCurrentPack
      ? current.packCooldownSeconds
      : (authoritative.packCooldownSeconds ?? current.packCooldownSeconds),
    packCharges: preserveCurrentPack ? current.packCharges : (authoritative.packCharges ?? current.packCharges),
    packMaxCharges: preserveCurrentPack ? current.packMaxCharges : (authoritative.packMaxCharges ?? current.packMaxCharges),
    packEnergy: preserveCurrentPack ? current.packEnergy : (authoritative.packEnergy ?? current.packEnergy),
    packOptions: preserveCurrentPack ? current.packOptions : (authoritative.packOptions.length ? authoritative.packOptions : current.packOptions),
    grenadeSlot: authoritative.grenadeSlot ?? current.grenadeSlot,
    grenadeId: authoritative.grenadeId ?? current.grenadeId,
    grenadeLabel: authoritative.grenadeLabel ?? current.grenadeLabel,
    grenadeType: authoritative.grenadeType ?? current.grenadeType,
    grenadeState: authoritative.grenadeState ?? current.grenadeState,
    grenadeActive: authoritative.grenadeActive ?? current.grenadeActive,
    grenadeReady: authoritative.grenadeReady ?? current.grenadeReady,
    grenadeCooldownSeconds: authoritative.grenadeCooldownSeconds ?? current.grenadeCooldownSeconds,
    grenadeCharges: authoritative.grenadeCharges ?? current.grenadeCharges,
    grenadeMaxCharges: authoritative.grenadeMaxCharges ?? current.grenadeMaxCharges,
    grenadeOptions: authoritative.grenadeOptions?.length ? authoritative.grenadeOptions : (current.grenadeOptions ?? []),
    botIntent: authoritative.botIntent ?? current.botIntent,
    weaponDebug: authoritative.weaponDebug ?? current.weaponDebug,
  };
}

function resolveMatch(snapshot: Partial<RichMatchSnapshot> | undefined): MatchState {
  return {
    ...DEFAULT_MATCH,
    ...(snapshot ?? {}),
    teamScores: {
      red: snapshot?.teamScores?.red ?? DEFAULT_MATCH.teamScores.red,
      blue: snapshot?.teamScores?.blue ?? DEFAULT_MATCH.teamScores.blue,
    },
    phase: snapshot?.phase ?? DEFAULT_MATCH.phase,
    countdownSeconds: snapshot?.countdownSeconds ?? DEFAULT_MATCH.countdownSeconds,
    winnerTeam: normalizeTeamId(snapshot?.winnerTeam),
    awards: normalizeAwards(snapshot?.awards),
  };
}

function resolveActiveDirective(snapshot: RichWorldSnapshot): TeamDirective | null {
  return (
    normalizeDirective(snapshot.activeDirective) ??
    normalizeDirective(snapshot.teamDirective) ??
    normalizeDirective(snapshot.directive) ??
    normalizeDirective(snapshot.directives) ??
    normalizeDirective(snapshot.match?.activeDirective) ??
    normalizeDirective(snapshot.match?.teamDirective) ??
    normalizeDirective(snapshot.match?.directive)
  );
}

function resolveCtfMacro(snapshot: RichWorldSnapshot): CtfMacroSummary | null {
  return summarizeCtfMacro(snapshot.ctfMacro) ?? summarizeCtfMacro(snapshot.match?.ctfMacro);
}

function resolveTacticalPings(snapshot: RichWorldSnapshot): TacticalPing[] {
  const worldPings =
    normalizeTacticalPings(snapshot.tacticalPings).length > 0
      ? normalizeTacticalPings(snapshot.tacticalPings)
      : normalizeTacticalPings(snapshot.pings);
  if (worldPings.length > 0) {
    return worldPings;
  }

  const matchPings =
    normalizeTacticalPings(snapshot.match?.tacticalPings).length > 0
      ? normalizeTacticalPings(snapshot.match?.tacticalPings)
      : normalizeTacticalPings(snapshot.match?.pings);
  return matchPings;
}

function normalizeScoreboardPlayers(value: unknown): ScoreboardPlayerState[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => {
      if (!entry || typeof entry !== 'object') {
        return null;
      }

      const player = entry as Record<string, unknown>;
      const id = normalizeString(player.id);
      if (!id) {
        return null;
      }

      return {
        id,
        displayName: normalizeString(player.displayName) ?? `Player ${id}`,
        team: normalizeTeamId(player.team),
        score: normalizeNumber(player.score) ?? 0,
        kills: normalizeNumber(player.kills) ?? 0,
        deaths: normalizeNumber(player.deaths) ?? 0,
        captures: normalizeNumber(player.captures) ?? 0,
        returns: normalizeNumber(player.returns) ?? 0,
        isBot: normalizeBoolean(player.isBot),
      };
    })
    .filter((player): player is ScoreboardPlayerState => Boolean(player));
}

export const useGameStore = create<GameStore>((set, get) => ({
  connected: false,
  connectionState: 'connecting',
  mapId: DEFAULT_MAP_ID,
  localId: null,
  localPlayer: {
    ...createDefaultPlayerState(),
    team: null,
    armorClass: null,
    selectedWeapon: 'spinfusor',
    ads: false,
    weaponHeat: 0,
    weaponCooldown: 0,
    overheated: false,
    spawnProtected: false,
    carryingFlagTeam: null,
    kills: 0,
    deaths: 0,
    captures: 0,
    returns: 0,
    packSlot: 1,
    packId: null,
    packLabel: 'Pack 1',
    packType: null,
    packState: 'ready',
    packActive: false,
    packReady: true,
    packCooldownSeconds: 0,
    packCharges: null,
    packMaxCharges: null,
    packEnergy: null,
    packOptions: [],
    grenadeSlot: 1,
    grenadeId: null,
    grenadeLabel: 'Grenade 1',
    grenadeType: null,
    grenadeState: 'ready',
    grenadeActive: false,
    grenadeReady: true,
    grenadeCooldownSeconds: 0,
    grenadeCharges: null,
    grenadeMaxCharges: null,
    grenadeOptions: [],
  },
  remotePlayers: {},
  scoreboardPlayers: [],
  projectiles: [],
  serverTick: 0,
  lastAppliedServerTick: 0,
  predictionBlendWeight: 0.78,
  match: DEFAULT_MATCH,
  flags: [],
  boostPads: [],
  generators: [],
  sensors: [],
  shieldDoors: [],
  inventoryStations: [],
  repairZones: [],
  turrets: [],
  effects: [],
  vehicles: [],
  deployables: [],
  packs: [],
  activeDirective: null,
  ctfMacro: null,
  tacticalPings: [],
  pingWaypoints: [],
  routeCaptureZones: [],
  objectiveScoringHints: [],
  baseFlagInteractionHooks: [],
  events: [],
  floatingDamages: [],
  killStreak: 0,
  lastKillTime: 0,
  lastHitDirection: null,
  lastHitTime: 0,
  gamePhase: 'menu',
  chatMessages: [],
  chatOpen: false,
  pendingArmorClass: null,
  pendingWeaponSlots: [],
  setPendingLoadout: (armorClass, weapons) => set({ pendingArmorClass: armorClass, pendingWeaponSlots: weapons }),
  clearPendingLoadout: () => set({ pendingArmorClass: null, pendingWeaponSlots: [] }),
  lastKillEvent: null,
  killReplayVisible: false,
  multiKillCount: 0,
  setKillEvent: (event) => {
    const now = Date.now();
    const state = get();
    const isChain = now - state.lastKillTime < 10000;
    const chainCount = isChain ? state.multiKillCount + 1 : 1;
    set({
      lastKillEvent: { ...event, isMultiKill: chainCount >= 2, multiKillCount: chainCount },
      killReplayVisible: true,
      multiKillCount: chainCount,
      lastKillTime: now,
    });
  },
  clearKillReplay: () => set({ killReplayVisible: false }),
  settings: loadSettings(),
  updateSettings: (partial) => {
    set((state) => {
      const next = { ...state.settings, ...partial };
      persistSettings(next);
      return { settings: next };
    });
  },
  siegeState: null,
  setSiegeState: (state: SiegeGameState | null) => set({ siegeState: state }),
  setGamePhase: (phase) => {
    const state = get();
    if (state.gamePhase === phase) {
      return;
    }

    if (phase === 'postmatch') {
      const local = state.localPlayer;
      const won = state.match.winnerTeam != null && state.match.winnerTeam === local.team;
      awardMatchXP(local.kills ?? 0, local.deaths ?? 0, local.captures ?? 0, won);
    }
    set({ gamePhase: phase });
  },
  addChatMessage: (msg) =>
    set((state) => ({ chatMessages: [...state.chatMessages, msg].slice(-40) })),
  setChatOpen: (open) => set({ chatOpen: open }),
  addFloatingDamage: (dmg) =>
    set((state) => ({ floatingDamages: [...state.floatingDamages, dmg] })),
  tickFloatingDamages: (dt) =>
    set((state) => ({
      floatingDamages: state.floatingDamages
        .map((d) => ({ ...d, age: d.age + dt }))
        .filter((d) => d.age < 1.8),
    })),
  recordLocalKill: (timestamp) =>
    set((state) => {
      // Simple streak: increment. Reset if more than 4s since last kill.
      const elapsed = timestamp - state.lastKillTime;
      const killStreak = elapsed < 4000 && state.lastKillTime > 0 ? state.killStreak + 1 : 1;
      return { killStreak, lastKillTime: timestamp };
    }),
  resetMatch: () =>
    set((state) => ({
      gamePhase: 'menu',
      match: {
        ...state.match,
        winnerTeam: null,
        countdownSeconds: DEFAULT_MATCH.countdownSeconds,
        phase: DEFAULT_MATCH.phase,
        teamScores: { red: DEFAULT_MATCH.teamScores.red, blue: DEFAULT_MATCH.teamScores.blue },
      },
      events: [],
      connected: false,
      connectionState: 'offline',
    })),
  setConnected: (connected) => set({ connected, connectionState: connected ? 'online' : 'offline' }),
  setConnectionState: (connectionState) => set({ connected: connectionState === 'online' || connectionState === 'standalone', connectionState }),
  startStandaloneSession: (options) => {
    const snapshot = createStandaloneWorldSnapshot(options);
    set({
      connected: true,
      connectionState: 'standalone',
      localId: 'local-standalone',
    });
    get().applySnapshot(snapshot as RichWorldSnapshot);
  },
  setWelcome: (id) =>
    set((state) => ({
      localId: id,
      localPlayer: { ...state.localPlayer, id },
    })),
  setLocalPlayer: (localPlayer) => {
    set((state) => {
      const nextLocalPlayer = {
        ...state.localPlayer,
        ...localPlayer,
      };

      if (isClientPlayerStateCoreEqual(state.localPlayer, nextLocalPlayer)) {
        if (
          shouldSyncWeaponDebug() &&
          localPlayer.weaponDebug != null &&
          !isDeepEqual(state.localPlayer.weaponDebug, localPlayer.weaponDebug)
        ) {
          return { localPlayer: { ...state.localPlayer, weaponDebug: localPlayer.weaponDebug } };
        }

        return {};
      }

      return { localPlayer: nextLocalPlayer };
    });
  },
  applySnapshot: (snapshot, predictionBlendWeightOverride) => {
    const state = get();
    const { localId, localPlayer, serverTick, predictionBlendWeight: storedPredictionBlendWeight } = state;
    const predictionBlendWeight =
      typeof predictionBlendWeightOverride === 'number' && Number.isFinite(predictionBlendWeightOverride)
        ? predictionBlendWeightOverride
        : storedPredictionBlendWeight;
    const incomingServerTick = Number.isFinite(snapshot.serverTick) ? snapshot.serverTick : 0;
    if (incomingServerTick < serverTick) {
      return;
    }

    const snapshotPlayers = Array.isArray(snapshot.players) ? snapshot.players : [];
    const scoreboardPlayers = normalizeScoreboardPlayers(snapshot.scoreboardPlayers ?? snapshot.players);
    const snapshotProjectiles = Array.isArray(snapshot.projectiles) ? snapshot.projectiles : [];
    const snapshotEvents = Array.isArray(snapshot.events) ? snapshot.events : [];
    const nextMatch = resolveMatch(snapshot.match);
    const nextFlags = normalizeWorldObjects(snapshot.flags);
    const nextBoostPads = normalizeWorldObjects(snapshot.boostPads);
    const nextGenerators = normalizeWorldObjects(snapshot.generators);
    const nextSensors = normalizeWorldObjects(snapshot.sensors);
    const nextShieldDoors = normalizeWorldObjects(snapshot.shieldDoors);
    const nextInventoryStations = normalizeWorldObjects(snapshot.inventoryStations);
    const nextRepairZones = normalizeWorldObjects(snapshot.repairZones);
    const nextTurrets = normalizeWorldObjects(snapshot.turrets);
    const nextEffects = normalizeWorldObjects(snapshot.effects);
    const nextVehicles = normalizeWorldObjects(snapshot.vehicles);
    const nextDeployables = normalizeWorldObjects(snapshot.deployables);
    const nextPacks = normalizeWorldObjects(snapshot.packs);
    const nextActiveDirective = resolveActiveDirective(snapshot);
    const nextCtfMacro = resolveCtfMacro(snapshot);
    const nextTacticalPings = resolveTacticalPings(snapshot);
    const nextPingWaypoints = normalizePingWaypoints((snapshot as RichWorldSnapshot & { pingWaypoints?: unknown }).pingWaypoints);
    const nextRouteCaptureZones = normalizeRouteCaptureZones(snapshot.routeCaptureZones);
    const nextObjectiveScoringHints = normalizeObjectiveScoringHints(snapshot.objectiveScoringHints);
    const nextBaseFlagInteractionHooks = normalizeBaseFlagInteractionHooks(snapshot.baseFlagInteractionHooks);
    const nextMapId = snapshot.mapId ?? DEFAULT_MAP_ID;
    const previousRemote = state.remotePlayers;
    const remotePlayers: Record<string, RemotePlayer> = {};
    let nextLocal = localPlayer;

    for (const player of snapshotPlayers) {
      const resolved = toClientPlayerState(player);

      if (player.id === localId) {
        nextLocal = resolveLocalPlayer(localPlayer, player, predictionBlendWeight);
      } else {
        // Phase 164 — exponential smoothing on remote player positions to
        // reduce snapshot jitter. Velocity-derived state (yaw/pitch/health)
        // is left authoritative; only the position vector is smoothed.
        const prev = previousRemote[player.id];
        if (prev) {
          const smoothing = 0.85;
          const px = prev.position.x * smoothing + resolved.position.x * (1 - smoothing);
          const py = prev.position.y * smoothing + resolved.position.y * (1 - smoothing);
          const pz = prev.position.z * smoothing + resolved.position.z * (1 - smoothing);
          resolved.position = { x: px, y: py, z: pz };
        }
        remotePlayers[player.id] = resolved;
      }
    }

    const isLocalPlayerChanged = !isClientPlayerStateCoreEqual(localPlayer, nextLocal);
    const isLocalPlayerWeaponDebugChanged = shouldSyncWeaponDebug() && !isDeepEqual(localPlayer.weaponDebug, nextLocal.weaponDebug);
    const shouldSyncLocalWeaponDebug = isLocalPlayerWeaponDebugChanged && nextLocal.weaponDebug != null;

    const nextState: Partial<GameStore> = {
      serverTick: incomingServerTick,
      lastAppliedServerTick: incomingServerTick,
    };

    if (isLocalPlayerChanged) {
      nextState.localPlayer = nextLocal;
    } else if (shouldSyncLocalWeaponDebug) {
      nextState.localPlayer = { ...localPlayer, weaponDebug: nextLocal.weaponDebug };
    }

    if (state.mapId !== nextMapId) {
      nextState.mapId = nextMapId;
    }
    if (!isDeepEqual(previousRemote, remotePlayers)) {
      nextState.remotePlayers = remotePlayers;
    }
    if (!isDeepEqual(state.scoreboardPlayers, scoreboardPlayers)) {
      nextState.scoreboardPlayers = scoreboardPlayers;
    }
    if (!isDeepEqual(state.projectiles, snapshotProjectiles)) {
      nextState.projectiles = snapshotProjectiles;
    }
    if (!isDeepEqual(state.match, nextMatch)) {
      nextState.match = nextMatch;
    }
    if (!isDeepEqual(state.flags, nextFlags)) {
      nextState.flags = nextFlags;
    }
    if (!isDeepEqual(state.boostPads, nextBoostPads)) {
      nextState.boostPads = nextBoostPads;
    }
    if (!isDeepEqual(state.generators, nextGenerators)) {
      nextState.generators = nextGenerators;
    }
    if (!isDeepEqual(state.sensors, nextSensors)) {
      nextState.sensors = nextSensors;
    }
    if (!isDeepEqual(state.shieldDoors, nextShieldDoors)) {
      nextState.shieldDoors = nextShieldDoors;
    }
    if (!isDeepEqual(state.inventoryStations, nextInventoryStations)) {
      nextState.inventoryStations = nextInventoryStations;
    }
    if (!isDeepEqual(state.repairZones, nextRepairZones)) {
      nextState.repairZones = nextRepairZones;
    }
    if (!isDeepEqual(state.turrets, nextTurrets)) {
      nextState.turrets = nextTurrets;
    }
    if (!isDeepEqual(state.effects, nextEffects)) {
      nextState.effects = nextEffects;
    }
    if (!isDeepEqual(state.vehicles, nextVehicles)) {
      nextState.vehicles = nextVehicles;
    }
    if (!isDeepEqual(state.deployables, nextDeployables)) {
      nextState.deployables = nextDeployables;
    }
    if (!isDeepEqual(state.packs, nextPacks)) {
      nextState.packs = nextPacks;
    }
    if (!isDeepEqual(state.activeDirective, nextActiveDirective)) {
      nextState.activeDirective = nextActiveDirective;
    }
    if (!isDeepEqual(state.ctfMacro, nextCtfMacro)) {
      nextState.ctfMacro = nextCtfMacro;
    }
    if (!isDeepEqual(state.tacticalPings, nextTacticalPings)) {
      nextState.tacticalPings = nextTacticalPings;
    }
    if (!isDeepEqual(state.pingWaypoints, nextPingWaypoints)) {
      nextState.pingWaypoints = nextPingWaypoints;
    }
    if (!isDeepEqual(state.routeCaptureZones, nextRouteCaptureZones)) {
      nextState.routeCaptureZones = nextRouteCaptureZones;
    }
    if (!isDeepEqual(state.objectiveScoringHints, nextObjectiveScoringHints)) {
      nextState.objectiveScoringHints = nextObjectiveScoringHints;
    }
    if (!isDeepEqual(state.baseFlagInteractionHooks, nextBaseFlagInteractionHooks)) {
      nextState.baseFlagInteractionHooks = nextBaseFlagInteractionHooks;
    }
    if (!isDeepEqual(state.events, snapshotEvents)) {
      nextState.events = snapshotEvents;
    }

    if (Object.keys(nextState).length === 0) {
      return;
    }

    set(nextState);
  },
}));

export function useSettings(): Settings {
  return useGameStore((state) => state.settings);
}

export function getSmokeStateSnapshot(): SmokeStateSnapshot {
  const state = useGameStore.getState();
  const position = state.localPlayer.position;

  return {
    gamePhase: state.gamePhase,
    connectionState: state.connectionState,
    connected: state.connected,
    localPosition: {
      x: position.x,
      y: position.y,
      z: position.z,
    },
    serverTick: state.serverTick,
    eventCount: state.events.length,
    effectCount: state.effects.length,
    mapId: state.mapId,
  };
}
